<template>
  <div class="text-base w-full">
    <dl class="p4 md:p-6">
      <o-description-details
        :term="$t('general.customer')"
        class="pb-2"
      >
        <o-collapse
          base-class="w-full border rounded-md"
          header-class="w-full flex justify-between items-center py-1 px-2 h-9"
          :disabled="isCourseRunCustomer || !$grants.all({ customer: ['profile']})"
        >
          <template #header>
            <div class="w-full flex items-center">
              <o-avatar :items="[reservationCustomer]" size="md" border="" class="flex-shrink-0" />
              <div class="ml-2 text-sm font-normal">
                <p class="leading-none">
                  <template v-if="reservationCustomer.customer.displayName">
                    {{ reservationCustomer.customer.displayName }}
                  </template>
                </p>
                <p class="mt-1 block text-xs text-gray-700 dark:text-gray-200 leading-none">
                  {{ reservationCustomer.customer.email }}
                </p>
              </div>
            </div>
          </template>

          <div class="px-3 py-2 border-t">
            <customer-preview :customer="reservationCustomer.customer" />
          </div>
        </o-collapse>
      </o-description-details>

      <o-description-details
        v-if="isCourseRunCustomer"
        term="Course"
      >
        <div class="relative  rounded-md border dark:border-gray-800 overflow-hidden">
          <OPanelLink
            :path="`/course/view?idCourseRun=${reservationCustomer.customerCourse.courseRun.idCourseRun}&idCourse=${reservationCustomer.customerCourse.course.idCourse}`"
            class="leading-none p-2 flex items-center justify-between border-b last:border-none hover:bg-primary-50 dark:hover:bg-gray-800/50 transition-all"
          >
            <div>
              <p class="font-medium text-sm mb-1">
                {{ reservationCustomer.customerCourse.course.name }}

                <span class="text-sm opacity-80 mb-1">
                  - {{ reservationCustomer.customerCourse.courseRun?.information }} <span class="font-normal opacity-70">(#{{ reservationCustomer.customerCourse.courseRun.idCourseRun }})</span>
                </span>
              </p>

              <p class="text-sm opacity-80">
                From {{ $date.convert(reservationCustomer.customerCourse.courseRun?.dates[0], undefined, $date.presets.long) }}
              </p>
            </div>
          </OPanelLink>
        </div>
      </o-description-details>

      <o-description-details
        v-if="!isCourseRunCustomer && isRecurring"
        :term="$t('reservation.recurrence')"
      >
        <div class="h-10 relative p-1 flex items-center justify-between rounded-md border">
          <div class="leading-none inline-block m-1">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('components.reservationCustomer.edit.info.recurrenceEnabled') }}
            </p>
          </div>

          <o-tooltip :text="$t('reservation.editRecurrence')">
            <o-button
              flat
              size="sm"
              icon="reservationRecurrence"
              class="inline-block"
              @click="handleRecurrence"
            />
          </o-tooltip>
        </div>
      </o-description-details>


      <o-description-details
        :term="$t('general.status')"
      >
        <status :reservationCustomer="reservationCustomer" @update:loading="loading" @update:reservationCustomer="update" @refresh="$emit('refresh')" />
      </o-description-details>

      <o-description-details
        v-if="!isCourseRunCustomer && reservationCustomer"
        :term="$t('general.price')"
      >
        <price :reservation="reservationCustomer" @update:reservation="update" />
      </o-description-details>

      <o-description-details
        v-if="!isCourseRunCustomer && (reservationCustomer.order || (!reservationCustomer.isPaid && !reservationCustomer.customerPass))"
        :term="$t('general.payment')"
      >
        <order :reservation="reservationCustomer" @update:loading="loading" @update:reservation="update" />
      </o-description-details>

      <o-description-details
        :term="$t('general.service')"
      >
        <div class="flex items-center justify-between border rounded-md p-2">
          <div class="flex items-center">
            <div
              class="self-stretch w-2 mr-2 rounded"
              :style="{
                backgroundColor: '#' + service.service.borderHexColour
              }"
            />
            <div class="w-full">
              <p>
                <span class="font-semibold">{{ service.service.title }}</span>
                {{ $t('general.withName', { name: service.employee.displayName }) }}
                <span v-if="service.substitutedEmployee">
                  {{ $t('components.reservationCustomer.edit.info.fillingIn', { name: service.substitutedEmployee.displayName }) }}
                </span>
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap">
                <span class="mr-4">
                  {{ $t('datetime.increments.mins', { mins: service.duration }, Number(service.duration)) }}
                </span>
                <span v-if="service.employee.displayName" class="mr-4">
                  {{ service.employee.displayName }}
                </span>
                <span v-for="resource in service.resources" :key="resource.isReservationServiceResource" class="mr-4">
                  {{ $t('general.colonSeparator', { label1: resource.name || resource.serviceResource.name, label2: resource.resource.displayName }) }}
                </span>
                <span v-if="reservationCustomer" class="mr-4">
                  {{ $currency.format(reservationCustomer.price) }}
                </span>
              </p>
            </div>
          </div>

          <div class="-mr-1 flex items-center space-x-1">
            <o-tooltip :text="$t('reservation.view')">
              <o-panel-link
                tag="o-button"
                flat
                size="sm"
                icon="view"
                class="inline-block"
                :path="`/appointment/${reservation.idReservation}?date=${reservation.date.value}`"
              />
            </o-tooltip>
            <o-tooltip v-if="!isCourseRunCustomer" :text="$t('service.edit')">
              <o-button
                flat
                size="sm"
                icon="edit"
                class="inline-block"
                @click="handleEditService"
              />
            </o-tooltip>
          </div>
        </div>
      </o-description-details>

      <template v-if="integrationsWithLabels">
        <o-description-details
          v-for="integration in integrationsWithLabels"
          :key="integration.integration.guid"
          :term="integration.integration.name"
        >
          <div v-for="label in integration.labels" :key="label.name">
            <span class="font-semibold">
              {{ $t('general.colon', { label: label.name }) }}
            </span>
            <span v-if="label.isText">
              {{ label.value }}
            </span>
            <a v-if="label.isUrl" :href="label.value" target="_blank">
              {{ label.value }}
            </a>
          </div>
        </o-description-details>
      </template>

      <o-description-details
        :term="$t('general.notes')"
        term-class="mt-2 text-sm leading-5 text-gray-500 dark:text-gray-400"
      >
        <o-field-group v-slot="{ status }" :name="$t('general.notes')" rules="max:1000">
          <TextAreaField
            v-model="_reservationCustomer.notes"
            size="sm"
            :placeholder="$t('reservation.addDetail')"
            base-class="-ml-2 w-full rounded-md border border-transparent hover:border-gray-200 focus:border-gray-200 dark:border-gray-800 dark:focus:border-gray-800 resize-none hover:resize focus:resize transition-colors duration-200"
            max-length="1000"
            :status="status"
          />
        </o-field-group>
      </o-description-details>
    </dl>

    <edit-service-modal
      ref="editServiceModal"
    />
  </div>
</template>

<script setup lang="ts">
import Status from '@/components/reservationCustomer/edit/Status.vue'
import Price from '@/components/reservation/edit/Price.vue'
import Order from '@/components/reservation/edit/Order.vue'
import EditServiceModal from '@/components/reservationCustomer/actions/EditService.vue'
import CustomerPreview from '@/components/customer/elements/Preview.vue'

import * as Fields from '@/components/fields'

import {
  OAvatar,
  ODescriptionDetails,
} from '@/components/elements'

const SelectField = Fields.SelectField
const TextField = Fields.TextField
const TextAreaField = Fields.TextAreaField

const { $api, $date, $grants, $currency } = useNuxtApp()

interface Props {
  reservationCustomer?: Record<string, any> | null
}

interface Emits {
  (e: 'update:reservationCustomer', value: any): void
  (e: 'update:loading', value: boolean): void
  (e: 'update'): void
  (e: 'recurrence'): void
  (e: 'refresh'): void
}

const props = withDefaults(defineProps<Props>(), {
  reservationCustomer: null,
})

const emit = defineEmits<Emits>()

const editServiceModal = ref<any>()

const edit = ref({
  price: false,
})
const test = ref(new Date())

const _reservationCustomer = computed({
  get () {
    return props.reservationCustomer
  },
  set (value) {
    emit('update:reservationCustomer', value)
  },
})

const reservation = computed(() => props.reservationCustomer!.reservation)
const service = computed(() => props.reservationCustomer!.reservation.services[0])

const start = computed(() => {
  const date = $date.parse(reservation.value.date.value)
  const time = service.value.startTime
  return $date.add(date, { minutes: time })
})

const finish = computed(() => {
  const date = $date.parse(reservation.value.date.value)
  const time = service.value.startTime + service.value.duration
  return $date.add(date, { minutes: time })
})

const date = computed(() => {
  return $date.format(start.value, $date.presets.full)
})

const time = computed(() => {
  const startFormatted = $date.format(start.value, $date.presets.time)
  const finishFormatted = $date.format(finish.value, $date.presets.time)
  return `${startFormatted} - ${finishFormatted}`
})

const integrationsWithLabels = computed(() => {
  if (props.reservationCustomer?.integrations) {
    return props.reservationCustomer.integrations.filter((integration: any) => integration.labels.length > 0)
  }
  return null
})

const isRecurring = computed(() => props.reservationCustomer?.isRecurring)
const isCourseRunCustomer = computed(() => !!props.reservationCustomer?.customerCourse)

function update (reservationCustomer: any) {
  emit('update:reservationCustomer', reservationCustomer)
}

async function moveReservation (reservation: any) {
  emit('update:loading', true)

  try {
    const response = await $api.reservationCustomer.get(
      String(props.reservationCustomer!.idReservationCustomer),
    )
    emit('update:reservationCustomer', response.data)
    emit('update:loading', false)
    emit('update')
  } catch (e) {
    emit('update:loading', false)
  }
}

function handleEditService () {
  editServiceModal.value.open(props.reservationCustomer, (updated: any) => {
    props.reservationCustomer!.resources = updated.resources
    props.reservationCustomer!.price = updated.price
  })
}

function handleRecurrence () {
  emit('recurrence')
}

function loading (loadingVal: boolean) {
  emit('update:loading', loadingVal)
}
</script>
